import { Platform } from "@angular/cdk/platform";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import {
  ActivatedRouteSnapshot,
  GuardsCheckEnd,
  GuardsCheckStart,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from "@angular/router";

import { ApplicationInsights } from "@microsoft/applicationinsights-web";

import { NgxLoadingModule } from "ngx-loading";
import { fromEvent } from "rxjs";
import { filter, first } from "rxjs/operators";

import { CanonicalUrlService, LinkInterceptor, MetaTagService, StructuredDataService } from "#modules/shared";
import { TranslatePipe } from "#modules/shared/i18n";

import { ConsentService } from "../../services/consent.service";
import { ConsentPopupComponent } from "../consent-popup/consent-popup.component";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxLoadingModule, RouterOutlet, TranslatePipe],
})
export class AppComponent {
  private readonly elRef = inject(ElementRef<HTMLElement>);
  readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  readonly platform = inject(Platform);
  readonly iconRegistry = inject(MatIconRegistry);
  readonly sanitizer = inject(DomSanitizer);
  private readonly dialog = inject(MatDialog);
  private readonly consentService = inject(ConsentService);
  private readonly linkInterceptor = inject(LinkInterceptor);
  private readonly canonicalUrlService = inject(CanonicalUrlService);
  private readonly metaTagService = inject(MetaTagService);
  private readonly structuredDataService = inject(StructuredDataService);
  private structuredDataPath: string | null = null;
  private readonly destroyRef = inject(DestroyRef);
  readonly loading = signal(true);

  private appInsights: ApplicationInsights;

  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  constructor() {
    const platform = this.platform;
    const iconRegistry = this.iconRegistry;
    const sanitizer = this.sanitizer;
    const consentService = this.consentService;

    iconRegistry.addSvgIcon("acodis-logo", sanitizer.bypassSecurityTrustResourceUrl("assets/images/acodis-logo-color.svg"));

    if (platform.IOS) {
      document.body.className += " ios";
    }

    if (!this.consentService.getState()) {
      this.dialog.open(ConsentPopupComponent, {
        hasBackdrop: false,
        closeOnNavigation: false,
        disableClose: true,
        position: { top: "72px", left: "0px", right: "0px", bottom: "auto" },
        panelClass: "consent-popup",
        maxWidth: "auto",
      });
    }

    // consentService.consent$
    //   .pipe(
    //     filter((consentGiven) => consentGiven),
    //     startWith(this.consentService.getState()),
    //     pairwise(),
    //     first()
    //   )
    //   .subscribe(([]) => {
    //     this.appInsights = new ApplicationInsights({
    //       config: { instrumentationKey: "<KEY>", enableDebug: true },
    //     });

    //     this.appInsights.loadAppInsights();
    //     this.appInsights.trackEvent({ name: "APP_LOADED" });
    //     this.appInsights.flush();
    //   });

    consentService.consent$.pipe(filter(Boolean), first(), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const sz = document.createElement("script");
      sz.type = "text/javascript";
      sz.async = true;

      sz.src = "//siteimproveanalytics.com/js/siteanalyze_6039428.js";

      const s = document.getElementsByTagName("script")[0];
      s.parentNode?.insertBefore(sz, s);
    });

    /** Hijack clicks outside of editor: navigate to internal URLs and open external URLs in a new tab */
    fromEvent<MouseEvent>(this.elRef.nativeElement, "click", { capture: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (location.pathname.includes("/editor/")) return;
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!target.closest("app-generic-page,app-library-viewer,app-home-page,app-cantons-page,app-communes-page")) return;
        const link = target.closest("a[href]:not([data-external-link])") as HTMLAnchorElement | undefined;

        if (!link) return;
        this.linkInterceptor.maybeRedirect(link, () => {
          event.stopImmediatePropagation();
          event.preventDefault();
        });
      });

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((routerEvent) => {
      switch (true) {
        case routerEvent instanceof NavigationStart:
        case routerEvent instanceof GuardsCheckEnd: {
          this.loading.set(true);
          break;
        }

        case routerEvent instanceof GuardsCheckStart:
        case routerEvent instanceof NavigationEnd:
        case routerEvent instanceof NavigationCancel:
        case routerEvent instanceof NavigationError: {
          this.loading.set(false);
          break;
        }
      }

      if (routerEvent instanceof NavigationStart) {
        // Only a real page change invalidates the graph. Query-only navigations (?page=1) fire after
        // the page already published its data, so clearing on those would wipe it.
        const path = routerEvent.url.split(/[?#]/)[0];
        if (path !== this.structuredDataPath) {
          this.structuredDataPath = path;
          this.structuredDataService.clear();
        }
      }

      if (routerEvent instanceof NavigationEnd) {
        this.canonicalUrlService.updateTag(routerEvent.urlAfterRedirects);
      }
    });

    // combineLatest([this.router.events, consentService.consent$]).subscribe(([routerEvent, consentGiven]) => {
    //   switch (true) {
    //     case routerEvent instanceof NavigationStart:
    //     case routerEvent instanceof GuardsCheckEnd: {
    //       this.loading = true;
    //       break;
    //     }

    //     case routerEvent instanceof GuardsCheckStart:
    //     case routerEvent instanceof NavigationEnd:
    //     case routerEvent instanceof NavigationCancel:
    //     case routerEvent instanceof NavigationError: {
    //       this.loading = false;
    //       break;
    //     }
    //     case routerEvent instanceof ResolveEnd: {
    //       if (consentGiven) {
    //         try {
    //           this.trackActivation(routerEvent as ResolveEnd);
    //         } catch (e) {
    //           console!.error(e);
    //         }
    //       }
    //       break;
    //     }
    //     default: {
    //       break;
    //     }
    //   }
    // });
  }

  ngOnInit(): void {
    this.metaTagService.resetTags();
  }

  // private trackActivation(resolveEndEvent: ResolveEnd) {
  //   /* No google tracking, insight */
  // }
}
